import React, { JSX } from 'react';
import {
    Calendar,
    CheckSquare,
    ChevronDown,
    Eye,
    FileText,
    FolderOpen,
    Globe2,
    LayoutList,
    MinusCircle,
} from 'lucide-react';
import RefreshButton from '../../shared/RefreshButton';
import type { LogFileListScope, LogType } from '../types/LogBrowseTypes';
import {
    logBrowsePanelDetailsClass,
    logBrowsePanelSummaryClass,
    logBrowseSelectClass,
    modeLabelForScope,
} from '../utils/logBrowseUi';

import type { LogBrowseFiltersProps } from '../types/LogViewerProps';

export type { LogBrowseFiltersProps };

export default function LogBrowseFilters(props: LogBrowseFiltersProps): JSX.Element {
    const {
        open,
        onOpenChange,
        isAdmin,
        isOperator,
        canUseEventMode,
        logFileListScope,
        onLogFileListScopeChange,
        logType,
        onLogTypeChange,
        logFolder,
        onLogFolderChange,
        filteredFolders,
        onRefreshFolders,
        busyFolders,
        adminViewEventId,
        onAdminViewEventIdChange,
        adminEventsForLogs,
        adminEventsForLogsBusy,
        operatorSelectedEventLabel,
        effectiveLogEventId,
        showRaceFilter,
        selectedRaceId,
        onSelectedRaceIdChange,
        raceOptions,
        raceOptionsBusy,
        logBrowseTargetSummary,
        selectedKeysCount,
        objectCount,
        busyList,
        onViewFiles,
    } = props;

    return (
        <details
            className={`${logBrowsePanelDetailsClass} shrink-0`}
            open={open}
            onToggle={(e) => onOpenChange(e.currentTarget.open)}
        >
            <summary className={logBrowsePanelSummaryClass}>
                <ChevronDown
                    className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-slate-400 transition-transform group-open:rotate-180"
                    aria-hidden
                />
                <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Filters</h2>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                        Mode, log type, and target — collapse this panel to save space.
                    </p>
                </div>
            </summary>
            <div className="p-4">
                <div className="min-w-0 space-y-3">
                    <div>
                        <label
                            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400"
                            htmlFor="log-browse-mode"
                        >
                            <LayoutList className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                            Mode
                        </label>
                        <div className="relative mt-1">
                            <select
                                id="log-browse-mode"
                                className={logBrowseSelectClass}
                                value={logFileListScope}
                                onChange={(e) =>
                                    onLogFileListScopeChange(e.target.value as LogFileListScope)
                                }
                            >
                                <option value="by_event">All files for event</option>
                                <option value="by_folder">One folder</option>
                                {isAdmin ? <option value="fleet_wide">Fleet-wide</option> : null}
                                <option value="no_folder">No folder</option>
                            </select>
                            <ChevronDown
                                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                                aria-hidden
                            />
                        </div>
                        {!canUseEventMode && logFileListScope === 'by_event' && (
                            <p className="mt-1 text-[11px] text-gray-700 dark:text-slate-300">
                                Select an event on the Events tab to use event mode.
                            </p>
                        )}
                    </div>

                    <div>
                        <label
                            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400"
                            htmlFor="log-browse-log-type"
                        >
                            <FileText className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                            Log type
                        </label>
                        <div className="relative mt-1">
                            <select
                                id="log-browse-log-type"
                                className={logBrowseSelectClass}
                                value={logType}
                                onChange={(e) => onLogTypeChange(e.target.value as LogType)}
                            >
                                <option value="race">Race</option>
                                <option value="debug">Debug</option>
                            </select>
                            <ChevronDown
                                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                                aria-hidden
                            />
                        </div>
                    </div>

                    <div>
                        <label
                            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400"
                            htmlFor={
                                logFileListScope === 'by_event'
                                    ? 'log-browse-event'
                                    : logFileListScope === 'by_folder'
                                        ? 'log-browse-folder'
                                        : logFileListScope === 'fleet_wide'
                                            ? 'log-browse-scope-fleet'
                                            : 'log-browse-scope-nofolder'
                            }
                        >
                            {logFileListScope === 'by_event' ? (
                                <>
                                    <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                                    Event
                                </>
                            ) : logFileListScope === 'by_folder' ? (
                                <>
                                    <FolderOpen className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                                    Folder
                                </>
                            ) : logFileListScope === 'fleet_wide' ? (
                                <>
                                    <Globe2 className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                                    Scope
                                </>
                            ) : (
                                <>
                                    <MinusCircle className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                                    Scope
                                </>
                            )}
                        </label>
                        {logFileListScope === 'by_event' ? (
                            isOperator && !isAdmin ? (
                                <div
                                    id="log-browse-event"
                                    className="mt-1 flex min-h-[2.5rem] items-center rounded-md border border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 px-3 py-2 text-sm break-words text-gray-900 dark:text-slate-100"
                                >
                                    {operatorSelectedEventLabel || 'No event selected'}
                                </div>
                            ) : (
                                <div className="relative mt-1">
                                    <select
                                        id="log-browse-event"
                                        className={logBrowseSelectClass}
                                        value={adminViewEventId}
                                        onChange={(e) => onAdminViewEventIdChange(e.target.value)}
                                        disabled={adminEventsForLogsBusy}
                                    >
                                        <option value="">Select event…</option>
                                        {adminEventsForLogs.map((ev) => (
                                            <option key={ev.eventId} value={ev.eventId}>
                                                {ev.name ? `${ev.name} (${ev.eventId})` : ev.eventId}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown
                                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                                        aria-hidden
                                    />
                                </div>
                            )
                        ) : logFileListScope === 'by_folder' ? (
                            <div className="mt-1 flex flex-wrap items-stretch gap-2">
                                <div className="relative min-w-[12rem] flex-1">
                                    <select
                                        id="log-browse-folder"
                                        className={logBrowseSelectClass}
                                        value={logFolder}
                                        onChange={(e) => onLogFolderChange(e.target.value)}
                                    >
                                        <option value="">Select folder…</option>
                                        {filteredFolders.map((f) => (
                                            <option key={f} value={f}>
                                                {f}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown
                                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                                        aria-hidden
                                    />
                                </div>
                                <RefreshButton
                                    busy={busyFolders}
                                    onClick={onRefreshFolders}
                                    className="shrink-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                                    busyText="Loading..."
                                />
                            </div>
                        ) : logFileListScope === 'fleet_wide' ? (
                            <div className="relative mt-1">
                                <select
                                    id="log-browse-scope-fleet"
                                    className={logBrowseSelectClass}
                                    disabled
                                    value="fleet"
                                    aria-label="Fleet-wide scope"
                                >
                                    <option value="fleet">Entire fleet — all indexed folders</option>
                                </select>
                                <ChevronDown
                                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300 dark:text-slate-600"
                                    aria-hidden
                                />
                            </div>
                        ) : (
                            <div className="relative mt-1">
                                <select
                                    id="log-browse-scope-nofolder"
                                    className={logBrowseSelectClass}
                                    disabled
                                    value="nofolder"
                                    aria-label="No-folder scope"
                                >
                                    <option value="nofolder">
                                        {effectiveLogEventId
                                            ? 'This event — uploads with no folder'
                                            : 'Fleet — uploads with no folder'}
                                    </option>
                                </select>
                                <ChevronDown
                                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300 dark:text-slate-600"
                                    aria-hidden
                                />
                            </div>
                        )}
                        {showRaceFilter ? (
                            <div className="mt-2">
                                <label
                                    className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400"
                                    htmlFor="log-browse-race"
                                >
                                    <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" aria-hidden />
                                    Race
                                </label>
                                <div className="relative mt-1">
                                    <select
                                        id="log-browse-race"
                                        className={logBrowseSelectClass}
                                        value={selectedRaceId}
                                        onChange={(e) => onSelectedRaceIdChange(e.target.value)}
                                        disabled={!effectiveLogEventId || raceOptionsBusy}
                                    >
                                        <option value="">All races</option>
                                        {raceOptions.map((race) => (
                                            <option key={race.raceId} value={race.raceId}>
                                                {race.name ? `${race.name} (${race.raceId})` : race.raceId}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown
                                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                                        aria-hidden
                                    />
                                </div>
                            </div>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-2">
                            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                <LayoutList className="h-3.5 w-3.5 shrink-0 text-violet-500 dark:text-violet-300" aria-hidden />
                                <span className="shrink-0 text-gray-500 dark:text-slate-400">Mode</span>
                                <span className="min-w-0 break-words">
                                    {modeLabelForScope(logFileListScope)}
                                </span>
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                <FileText className="h-3.5 w-3.5 shrink-0 text-sky-500 dark:text-sky-300" aria-hidden />
                                <span className="text-gray-500 dark:text-slate-400">Type</span>
                                {logType.toUpperCase()}
                            </span>
                            <span className="inline-flex max-w-full items-start gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                {logFileListScope === 'fleet_wide' ? (
                                    <Globe2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-hidden />
                                ) : logFileListScope === 'no_folder' ? (
                                    <MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-hidden />
                                ) : logFileListScope === 'by_folder' ? (
                                    <FolderOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-hidden />
                                ) : (
                                    <Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" aria-hidden />
                                )}
                                <span className="min-w-0 flex-1 break-words">
                                    <span className="text-gray-500 dark:text-slate-400">Target </span>
                                    {logBrowseTargetSummary}
                                </span>
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                                <CheckSquare className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-300" aria-hidden />
                                {selectedKeysCount} selected
                            </span>
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-200 dark:border-slate-700 pt-4">
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                        {busyList
                            ? 'Loading files…'
                            : objectCount > 0
                                ? `${objectCount} file${objectCount !== 1 ? 's' : ''} match these filters`
                                : 'No files loaded yet'}
                    </p>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={onViewFiles}
                        disabled={busyList}
                    >
                        <Eye className="h-4 w-4 shrink-0" aria-hidden />
                        View Files
                    </button>
                </div>
            </div>
        </details>
    );
}
